'use client';

import { Subject } from '@/hooks/useSubject';
import { Dialog } from '@headlessui/react';
import { useLayoutEffect, useState } from 'react';
import { mdiArrowRight } from '@mdi/js';
import TextButton from '@/components/Button/TextButton';
import { motion, useReducedMotion } from 'framer-motion';
import { EmbeddableTextAccessibilitySettings } from '../AccessibilityMenu/AccessibilityMenu';
import { useSubjectProgress } from '@/hooks/useSubjectProgress';
import { useStrings } from '@/resources/translations/translations';
import SingleBlock from '../Block/SingleBlock';
import { measure } from '../Analytics/Analytics';

const StoryIntro = ({ subject }: { subject: Subject }) => {
    const reduceMotion = useReducedMotion();
    const strings = useStrings('components.StoryIntro');

    const { subjectProgress, setHasSeenIntro } = useSubjectProgress(subject.id);

    // Only show after first render, to fix SSR issues
    const [canShowIntro, setCanShowIntro] = useState(false);
    useLayoutEffect(() => {
        setCanShowIntro(true);
    }, []);

    return (
        <Dialog
            open={canShowIntro && !subjectProgress.hasSeenIntro}
            onClose={() => setHasSeenIntro(true)}
        >
            <Dialog.Overlay className="fixed inset-0 bg-secondary/50 filter backdrop-blur-lg z-200" />
            <Dialog.Panel className="fixed inset-0 w-full h-full overflow-y-auto z-200">
                <div className="grid items-center justify-stretch min-h-screen px-4 py-12">
                    <motion.div
                        className="row-start-1 row-end-1 col-start-1 col-end-1 max-w-sm w-full mx-auto bg-white rounded-xl shadow-2xl overflow-hidden"
                        initial={{
                            opacity: 0,
                            scale: 0.9,
                            x: !reduceMotion ? '25%' : 0,
                        }}
                        animate={{
                            opacity: 1,
                            scale: 1,
                            x: 0,
                            transition: { duration: 0.3 },
                        }}
                    >
                        <Dialog.Title
                            aria-hidden="true"
                            className="flex items-center justify-center p-6 bg-secondary/20"
                        >
                            <SingleBlock
                                data={{
                                    text: strings('intro_chat_message'),
                                }}
                                type="chat_text"
                                isAnimated={false}
                            />
                        </Dialog.Title>
                        <div className="px-4 pb-4">
                            <div className="-mx-4">
                                <ul className="grid grid-cols-2 gap-2 p-2">
                                    <EmbeddableTextAccessibilitySettings />
                                </ul>
                                <hr className="mb-4" />
                            </div>

                            <TextButton
                                className="w-full"
                                onClick={() => {
                                    measure(`story_intro_begin_${subject.id}`);
                                    setHasSeenIntro(true);
                                }}
                                icon={mdiArrowRight}
                            >
                                {strings('begin_button')}
                            </TextButton>
                        </div>
                    </motion.div>
                </div>
            </Dialog.Panel>
        </Dialog>
    );
};

export default StoryIntro;
